import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Plus, FileText, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LessonPlans() {
  const navigate = useNavigate();
  const [lessonPlans, setLessonPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLessonPlans();
  }, []);

  const fetchLessonPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/teacher/lesson-plans`);
      setLessonPlans(response.data);
    } catch (error) {
      toast.error('Failed to load lesson plans');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sky-50" data-testid="lesson-plans-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <Button
            onClick={() => navigate('/lesson-plans/new')}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="create-lesson-plan-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Lesson Plan
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-slate-900 mb-2">My Lesson Plans</h2>
          <p className="text-lg text-slate-600">CAPS-aligned lesson plans for your classes</p>
        </div>

        {loading ? (
          <div className="glass-card p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          </div>
        ) : lessonPlans.length === 0 ? (
          <div className="glass-card p-12 text-center" data-testid="no-lesson-plans">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Lesson Plans Yet</h3>
            <p className="text-slate-600 mb-6">Create your first CAPS-aligned lesson plan</p>
            <Button
              onClick={() => navigate('/lesson-plans/new')}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Lesson Plan
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lessonPlans.map(plan => (
              <button
                key={plan.plan_id}
                onClick={() => navigate(`/lesson-plans/${plan.plan_id}`)}
                className="glass-card-hover p-6 text-left"
                data-testid={`lesson-plan-${plan.plan_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-teal-600" />
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    plan.status === 'final'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {plan.status}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {plan.subject} - {plan.grade}
                </h3>
                <p className="text-slate-600 mb-4">Duration: {plan.duration}</p>
                <div className="flex items-center text-sm text-slate-500">
                  <Calendar className="w-4 h-4 mr-1" />
                  {new Date(plan.created_at).toLocaleDateString()}
                </div>
                <div className="mt-4 text-sm text-slate-600">
                  {plan.lessons.length} lesson{plan.lessons.length !== 1 ? 's' : ''}
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}